import { useState, useMemo, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  HelpCircle,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Search,
  Users,
  BookOpen,
  Mail,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/Card";
import { PortableTextContent } from "../components/news/PortableTextContent";
import { useFaqs } from "../lib/hooks/useFaqs";
import type { FaqCategoryWithItems, FaqItem } from "../lib/sanity";

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  membership: Users,
  training: BookOpen,
};
const DEFAULT_CATEGORY_ICON = HelpCircle;

function getCategoryIcon(name: string) {
  const key = name.toLowerCase().replace(/\s+/g, "");
  return CATEGORY_ICONS[key] ?? DEFAULT_CATEGORY_ICON;
}

function CategoryCards({
  categories,
  searchQuery,
}: {
  categories: FaqCategoryWithItems[];
  searchQuery: string;
}) {
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categories.filter((c) => (c.faqs ?? []).length > 0);
    return categories.filter((cat) => {
      const nameMatch = cat.name.toLowerCase().includes(q);
      const questionMatch = (cat.faqs ?? []).some((f) =>
        f.question.toLowerCase().includes(q),
      );
      return (nameMatch || questionMatch) && (cat.faqs ?? []).length > 0;
    });
  }, [categories, searchQuery]);

  if (filtered.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {filtered.map((category) => {
        const IconComponent = getCategoryIcon(category.name);
        return (
          <Link
            key={category._id}
            to={`/faqs?category=${encodeURIComponent(category._id)}`}
            className="group rounded-xl border border-gray-200 bg-white p-6 text-left shadow-sm transition-colors hover:border-primary-200 hover:bg-primary-50/30"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <IconComponent className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-gray-900 group-hover:text-primary-700">
                  {category.name}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Browse questions in this category
                </p>
                <span className="mt-3 inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700">
                  Browse questions
                  <ArrowRight className="ml-1 h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function PopularQuestions({
  categories,
  searchQuery,
}: {
  categories: FaqCategoryWithItems[];
  searchQuery: string;
}) {
  const flatQuestions = useMemo(() => {
    const out: { category: FaqCategoryWithItems; item: FaqItem }[] = [];
    for (const cat of categories) {
      for (const item of cat.faqs ?? []) {
        out.push({ category: cat, item });
      }
    }
    return out;
  }, [categories]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      const markedPopular = flatQuestions.filter((x) => x.item.popular === true);
      return markedPopular.length > 0
        ? markedPopular.slice(0, 8)
        : flatQuestions.slice(0, 3);
    }
    return flatQuestions
      .filter(
        (x) =>
          x.item.question.toLowerCase().includes(q) ||
          x.category.name.toLowerCase().includes(q),
      )
      .slice(0, 5);
  }, [flatQuestions, searchQuery]);

  const popular = filtered;
  if (popular.length === 0) return null;

  return (
    <Card variant="elevated" className="rounded-xl bg-white">
      <CardContent className="p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Popular questions
        </h2>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-gray-700">
          {popular.map(({ category, item }) => (
            <li key={`${category._id}-${item._key}`}>
              <Link
                to={`/faqs?category=${encodeURIComponent(category._id)}&open=${encodeURIComponent(item._key)}`}
                className="hover:text-primary-600 hover:underline"
              >
                {item.question}
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function QuestionAccordion({
  item,
  defaultOpen = false,
}: {
  item: FaqItem;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center gap-4 p-4 text-left hover:bg-gray-50/50 transition-colors"
        aria-expanded={open}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <HelpCircle className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1 font-medium text-gray-900">
          {item.question}
        </span>
        <span className="shrink-0 text-gray-400">
          {open ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-2 text-gray-600 text-sm [&_.mb-4]:mb-2">
          {Array.isArray(item.answer) ? (
            <PortableTextContent value={item.answer} />
          ) : (
            <p className="text-gray-600">{item.answer}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function Faqs() {
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const openKey = searchParams.get("open");
  const [searchQuery, setSearchQuery] = useState("");
  const { data: categories, isLoading, isError } = useFaqs();

  const selectedCategory =
    categoryId && categories?.find((c) => c._id === categoryId);
  const hasCategorySelected = Boolean(selectedCategory);

  const filteredCategoryFaqs = useMemo((): FaqItem[] => {
    if (!selectedCategory || !selectedCategory.faqs) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return selectedCategory.faqs;
    return selectedCategory.faqs.filter((f: FaqItem) =>
      f.question.toLowerCase().includes(q),
    );
  }, [selectedCategory, searchQuery]);

  const searchPlaceholder = hasCategorySelected && selectedCategory
    ? `Search ${selectedCategory.name.toLowerCase()} questions...`
    : "Search FAQs...";

  const openQuestionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (openKey && selectedCategory && openQuestionRef.current) {
      openQuestionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [openKey, hasCategorySelected]);

  return (
    <div className="space-y-5">
      {/* Title, subtitle, search */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">FAQs</h1>
        <p className="text-gray-600 mt-1">
          Find answers to common questions about membership, training and events
          at North Lincolnshire and Humberside Sailing Club.
        </p>
        <div className="relative mt-6 max-w-xl">
          <Search
            className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            type="search"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            aria-label="Search FAQs"
          />
        </div>
      </div>

      {/* Back to categories - when viewing a category */}
      {hasCategorySelected && (
        <div>
          <Link
            to="/faqs"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to categories
          </Link>
        </div>
      )}

      {isLoading && (
        <div className="text-center py-12 text-gray-600">Loading FAQs…</div>
      )}

      {isError && (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            Something went wrong loading the FAQs. Please try again later.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && (!categories || categories.length === 0) && (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            No FAQ categories yet.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && (categories?.length ?? 0) > 0 && (
        <>
          {!hasCategorySelected && (
            <>
              <PopularQuestions
                categories={categories ?? []}
                searchQuery={searchQuery}
              />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Choose a category
                </h2>
                <CategoryCards
                  categories={categories ?? []}
                  searchQuery={searchQuery}
                />
              </div>
            </>
          )}

          {hasCategorySelected && selectedCategory && (
            <div className="space-y-6">
              <Card variant="elevated" className="rounded-xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-6">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      {(() => {
                        const IconComponent = getCategoryIcon(
                          selectedCategory.name,
                        );
                        return <IconComponent className="h-6 w-6" />;
                      })()}
                    </span>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {selectedCategory.name}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        Questions in this category
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {filteredCategoryFaqs.length === 0 ? (
                      <p className="py-8 text-center text-gray-600">
                        {searchQuery.trim()
                          ? "No questions match your search. Try a different term or clear the search."
                          : "No questions in this category yet."}
                      </p>
                    ) : (
                      filteredCategoryFaqs.map((item: FaqItem) => {
                        const isOpenByUrl = openKey === item._key;
                        return (
                          <div
                            key={item._key}
                            ref={isOpenByUrl ? openQuestionRef : undefined}
                          >
                            <QuestionAccordion
                              item={item}
                              defaultOpen={isOpenByUrl}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {categoryId && !selectedCategory && (
            <Card>
              <CardContent className="py-12 text-center text-gray-600">
                Category not found.{" "}
                <Link
                  to="/faqs"
                  className="font-medium text-primary-600 hover:text-primary-700"
                >
                  Choose a category
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Still need help */}
          <section className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-gray-700">
              Still need help? If your question isn&apos;t listed, contact us.
            </p>
            <Link
              to="/contact_us"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <Mail className="h-4 w-4" />
              Contact the club
            </Link>
          </section>
        </>
      )}
    </div>
  );
}
